import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { ImageField } from "./ImageField";
import { useAdminT } from "./adminI18n";

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

const COLOR_FIELDS = [
  { key: "bg", label: "لون الخلفية الرئيسي" },
  { key: "bg_alt", label: "لون الخلفية البديل (الأقسام)" },
  { key: "surface", label: "لون البطاقات" },
  { key: "accent", label: "اللون الذهبي المميز" },
  { key: "text", label: "لون النص الرئيسي" },
  { key: "muted", label: "لون النص الخافت" },
];

const HOME_SECTIONS = [
  { id: "marquee", label: "شريط العبارات المتحرك" },
  { id: "about", label: "عن مايا" },
  { id: "courses", label: "الدورات" },
  { id: "consultations", label: "الاستشارات 1:1" },
  { id: "journey", label: "رحلة التشافي" },
  { id: "testimonials", label: "آراء العملاء" },
  { id: "clients", label: "نخبة العملاء" },
  { id: "blog", label: "قسم المقالات" },
  { id: "faq", label: "الأسئلة الشائعة" },
  { id: "banners", label: "لافتات الفعاليات القادمة" },
  { id: "cta_banner", label: "لافتة الدعوة الختامية" },
  { id: "contact", label: "قسم التواصل" },
  { id: "whatsapp_float", label: "زر واتساب العائم" },
];

const HOME_ELEMENTS = [
  { id: "header_whatsapp", label: "زر واتساب في الشريط العلوي" },
  { id: "header_login", label: "زر تسجيل الدخول في الشريط العلوي" },
  { id: "header_cta", label: "زر «ابدأ الآن» في الشريط العلوي" },
  { id: "hero_primary", label: "الزر الرئيسي في الواجهة" },
  { id: "hero_secondary", label: "زر واتساب في الواجهة" },
  { id: "hero_stats", label: "أرقام الإحصاءات في الواجهة" },
  { id: "hero_features", label: "شريط المزايا أسفل الواجهة" },
  { id: "footer_social", label: "أيقونات التواصل في التذييل" },
];

export const SettingsTab = () => {
  const { t } = useAdminT();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newBtn, setNewBtn] = useState({ label: "", url: "", location: "hero", style: "gold" });

  useEffect(() => {
    axios.get(`${API}/settings`).then((res) => {
      const d = res.data;
      setForm({
        ...d,
        marquee_text: (d.marquee_phrases || []).join("\n"),
        marquee_text_en: (d.marquee_phrases_en || []).join("\n"),
      });
    }).catch(() => toast.error(t("تعذر تحميل الإعدادات")));
  }, [t]);

  if (!form) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>;
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setColor = (k) => (e) => setForm({ ...form, colors: { ...form.colors, [k]: e.target.value } });
  const setStat = (i, k) => (e) => {
    const stats = [...(form.stats || [])];
    stats[i] = { ...stats[i], [k]: e.target.value };
    setForm({ ...form, stats });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        marquee_phrases: form.marquee_text.split("\n").map((s) => s.trim()).filter(Boolean),
        marquee_phrases_en: (form.marquee_text_en || "").split("\n").map((s) => s.trim()).filter(Boolean),
      };
      delete payload.marquee_text;
      delete payload.marquee_text_en;
      await axios.put(`${API}/admin/settings`, payload, { headers: authHeaders() });
      toast.success(t("تم حفظ الإعدادات — حدّثي الصفحة الرئيسية لرؤية التغييرات"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="admin-settings-form" onSubmit={save} className="space-y-10">
      <section className="bg-luxury-surface border border-luxury-border p-8">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("ألوان الموقع")}</h2>
        <p className="text-luxury-gray text-sm mb-6">{t("غيّري هوية الموقع بالكامل من هنا — التغييرات تظهر مباشرة على الصفحة الرئيسية بعد الحفظ والتحديث.")}</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {COLOR_FIELDS.map((f) => (
            <div key={f.key} data-testid={`color-field-${f.key}`} className="flex items-center gap-3 bg-luxury-black border border-luxury-border p-3">
              <input
                type="color"
                data-testid={`color-input-${f.key}`}
                value={form.colors?.[f.key] || "#000000"}
                onChange={setColor(f.key)}
                className="w-12 h-12 cursor-pointer bg-transparent border-0 p-0"
              />
              <div>
                <p className="text-luxury-bone text-sm">{t(f.label)}</p>
                <p className="text-luxury-gray text-xs" dir="ltr">{form.colors?.[f.key]}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-5">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("نصوص الصفحة الرئيسية")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div><label className="block text-sm text-luxury-gray mb-2">{t("عنوان الهيرو — سطر ١")}</label><input data-testid="settings-hero-line1" value={form.hero_line1 || ""} onChange={set("hero_line1")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">{t("عنوان الهيرو — سطر ٢")}</label><input data-testid="settings-hero-line2" value={form.hero_line2 || ""} onChange={set("hero_line2")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">{t("عنوان الهيرو — سطر ٣ (ذهبي)")}</label><input data-testid="settings-hero-line3" value={form.hero_line3 || ""} onChange={set("hero_line3")} className={inputCls} /></div>
        </div>
        <div><label className="block text-sm text-luxury-gray mb-2">{t("النص التعريفي تحت العنوان")}</label><textarea data-testid="settings-hero-subtext" value={form.hero_subtext || ""} onChange={set("hero_subtext")} rows={3} className={inputCls} /></div>
        <div><label className="block text-sm text-luxury-gray mb-2">{t("عنوان قسم «عن مايا»")}</label><input data-testid="settings-about-title" value={form.about_title || ""} onChange={set("about_title")} className={inputCls} /></div>
        <div><label className="block text-sm text-luxury-gray mb-2">{t("نص قسم «عن مايا»")}</label><textarea data-testid="settings-about-text" value={form.about_text || ""} onChange={set("about_text")} rows={4} className={inputCls} /></div>
        <div><label className="block text-sm text-luxury-gray mb-2">{t("عبارات الشريط المتحرك (عبارة في كل سطر)")}</label><textarea data-testid="settings-marquee" value={form.marquee_text || ""} onChange={set("marquee_text")} rows={5} className={inputCls} /></div>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-5">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("النصوص الإنجليزية — English content")}</h2>
        <p className="text-luxury-gray text-sm">{t("تظهر هذه النصوص عندما يختار الزائر النسخة الإنجليزية من الموقع.")}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div><label className="block text-sm text-luxury-gray mb-2">Hero headline — line 1 (EN)</label><input dir="ltr" data-testid="settings-hero-line1-en" value={form.hero_line1_en || ""} onChange={set("hero_line1_en")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">Hero headline — line 2 (EN)</label><input dir="ltr" value={form.hero_line2_en || ""} onChange={set("hero_line2_en")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">Hero headline — line 3 gold (EN)</label><input dir="ltr" value={form.hero_line3_en || ""} onChange={set("hero_line3_en")} className={inputCls} /></div>
        </div>
        <div><label className="block text-sm text-luxury-gray mb-2">Hero subtext (EN)</label><textarea dir="ltr" value={form.hero_subtext_en || ""} onChange={set("hero_subtext_en")} rows={3} className={inputCls} /></div>
        <div><label className="block text-sm text-luxury-gray mb-2">About title (EN)</label><input dir="ltr" value={form.about_title_en || ""} onChange={set("about_title_en")} className={inputCls} /></div>
        <div><label className="block text-sm text-luxury-gray mb-2">About text (EN)</label><textarea dir="ltr" value={form.about_text_en || ""} onChange={set("about_text_en")} rows={4} className={inputCls} /></div>
        <div><label className="block text-sm text-luxury-gray mb-2">Marquee phrases (EN) — one per line</label><textarea dir="ltr" value={form.marquee_text_en || ""} onChange={set("marquee_text_en")} rows={5} className={inputCls} /></div>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-5">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("معلومات التواصل")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><label className="block text-sm text-luxury-gray mb-2">{t("رقم واتساب (بصيغة دولية بدون +)")}</label><input data-testid="settings-whatsapp" dir="ltr" value={form.whatsapp || ""} onChange={set("whatsapp")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">{t("رسالة واتساب الافتراضية")}</label><input data-testid="settings-whatsapp-message" value={form.whatsapp_message || ""} onChange={set("whatsapp_message")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">{t("رسالة واتساب بالإنجليزية (EN)")}</label><input dir="ltr" value={form.whatsapp_message_en || ""} onChange={set("whatsapp_message_en")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">{t("البريد الإلكتروني")}</label><input data-testid="settings-email" dir="ltr" value={form.email || ""} onChange={set("email")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">{t("رابط فيسبوك")}</label><input data-testid="settings-facebook" dir="ltr" value={form.facebook || ""} onChange={set("facebook")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">{t("رابط انستغرام")}</label><input data-testid="settings-instagram" dir="ltr" value={form.instagram || ""} onChange={set("instagram")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">{t("رابط انستغرام الثاني")}</label><input data-testid="settings-instagram2" dir="ltr" value={form.instagram2 || ""} onChange={set("instagram2")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">{t("رابط يوتيوب")}</label><input data-testid="settings-youtube" dir="ltr" value={form.youtube || ""} onChange={set("youtube")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">{t("رابط تيك توك")}</label><input data-testid="settings-tiktok" dir="ltr" value={form.tiktok || ""} onChange={set("tiktok")} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">{t("بريد الدعم الفني")}</label><input data-testid="settings-support-email" dir="ltr" value={form.support_email || ""} onChange={set("support_email")} className={inputCls} /></div>
        </div>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-5">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("الاقتباس والإحصائيات")}</h2>
        <p className="text-luxury-gray text-sm">{t("اقتباس كبير يظهر في منتصف الصفحة الرئيسية، وثلاث إحصائيات تظهر أسفل الواجهة الرئيسية.")}</p>
        <div><label className="block text-sm text-luxury-gray mb-2">{t("نص الاقتباس (عربي)")}</label><textarea data-testid="settings-quote" value={form.quote_text || ""} onChange={set("quote_text")} rows={2} className={inputCls} /></div>
        <div><label className="block text-sm text-luxury-gray mb-2">Quote (EN)</label><textarea dir="ltr" value={form.quote_text_en || ""} onChange={set("quote_text_en")} rows={2} className={inputCls} /></div>
        <div className="space-y-4">
          {(form.stats || []).map((st, i) => (
            <div key={i} data-testid={`stat-row-${i}`} className="grid grid-cols-2 md:grid-cols-4 gap-4 border border-luxury-border p-4">
              <div><label className="block text-xs text-luxury-gray mb-1">{t("القيمة")}</label><input value={st.value || ""} onChange={setStat(i, "value")} className={inputCls} /></div>
              <div><label className="block text-xs text-luxury-gray mb-1">Value (EN)</label><input dir="ltr" value={st.value_en || ""} onChange={setStat(i, "value_en")} className={inputCls} /></div>
              <div><label className="block text-xs text-luxury-gray mb-1">{t("الوصف")}</label><input value={st.label || ""} onChange={setStat(i, "label")} className={inputCls} /></div>
              <div><label className="block text-xs text-luxury-gray mb-1">Label (EN)</label><input dir="ltr" value={st.label_en || ""} onChange={setStat(i, "label_en")} className={inputCls} /></div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-5">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("صورة الموقع الرئيسية")}</h2>
        <p className="text-luxury-gray text-sm">{t("الصورة الكبيرة في واجهة الموقع وقسم «عن مايا» — ارفعي صورة جديدة من جهازك متى شئتِ.")}</p>
        <ImageField testid="settings-portrait-field" label={t("الصورة الرئيسية")} value={form.portrait_url} onChange={(v) => setForm({ ...form, portrait_url: v })} />
        <ImageField testid="settings-hero-bg-field" label={t("خلفية الافتتاحية (اختياري — تظهر خلف النص وصورة مايا)")} value={form.hero_bg} onChange={(v) => setForm({ ...form, hero_bg: v })} />
        <ImageField testid="settings-about-image-field" label={t("صورة قسم «عن مايا» (اختياري — الافتراضي: الصورة الرئيسية)")} value={form.about_image} onChange={(v) => setForm({ ...form, about_image: v })} />
        <ImageField testid="settings-courses-image-field" label={t("صورة قسم الدورات")} value={form.courses_image} onChange={(v) => setForm({ ...form, courses_image: v })} />
        <ImageField testid="settings-quote-bg-field" label={t("خلفية شريط الاقتباس (اختياري — تدعم الصور الشفافة PNG)")} value={form.quote_bg} onChange={(v) => setForm({ ...form, quote_bg: v })} />
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-4">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("أقسام الصفحة الرئيسية")}</h2>
        <p className="text-luxury-gray text-sm">{t("تحكمي في الأقسام الظاهرة على الصفحة الرئيسية — أخفي أي قسم متى شئتِ وأعيدي إظهاره لاحقاً.")}</p>
        {HOME_SECTIONS.map((sec) => {
          const isHidden = (form.hidden_sections || []).includes(sec.id);
          return (
            <div key={sec.id} data-testid={`section-toggle-${sec.id}`} className="flex items-center justify-between border border-luxury-border px-5 py-3.5">
              <span className="text-luxury-bone">{t(sec.label)}</span>
              <button
                type="button"
                data-testid={`section-toggle-btn-${sec.id}`}
                onClick={() => {
                  const cur = form.hidden_sections || [];
                  setForm({ ...form, hidden_sections: isHidden ? cur.filter((x) => x !== sec.id) : [...cur, sec.id] });
                }}
                className={`flex items-center gap-2 text-sm px-4 py-2 rounded-full transition-colors ${isHidden ? "border border-red-400/50 text-red-400" : "bg-luxury-gold/15 border border-luxury-gold/50 text-luxury-gold"}`}
              >
                {isHidden ? <><EyeOff className="w-4 h-4" strokeWidth={1.5} /> {t("مخفي")}</> : <><Eye className="w-4 h-4" strokeWidth={1.5} /> {t("ظاهر")}</>}
              </button>
            </div>
          );
        })}
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-4">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("أزرار وعناصر الصفحة")}</h2>
        <p className="text-luxury-gray text-sm">{t("أخفي أو أظهري أي زر أو عنصر — التحكم الكامل بيدك.")}</p>
        {HOME_ELEMENTS.map((el) => {
          const isHidden = (form.hidden_elements || []).includes(el.id);
          return (
            <div key={el.id} data-testid={`element-toggle-${el.id}`} className="flex items-center justify-between border border-luxury-border px-5 py-3.5">
              <span className="text-luxury-bone">{t(el.label)}</span>
              <button
                type="button"
                data-testid={`element-toggle-btn-${el.id}`}
                onClick={() => {
                  const cur = form.hidden_elements || [];
                  setForm({ ...form, hidden_elements: isHidden ? cur.filter((x) => x !== el.id) : [...cur, el.id] });
                }}
                className={`flex items-center gap-2 text-sm px-4 py-2 rounded-full transition-colors ${isHidden ? "border border-red-400/50 text-red-400" : "bg-luxury-gold/15 border border-luxury-gold/50 text-luxury-gold"}`}
              >
                {isHidden ? <><EyeOff className="w-4 h-4" strokeWidth={1.5} /> {t("مخفي")}</> : <><Eye className="w-4 h-4" strokeWidth={1.5} /> {t("ظاهر")}</>}
              </button>
            </div>
          );
        })}
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-5" data-testid="custom-buttons-section">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("أزرار مخصصة")}</h2>
        <p className="text-luxury-gray text-sm">{t("أضيفي أزرارك الخاصة — نص + رابط — وتظهر في الواجهة الرئيسية أو الشريط العلوي للموقع.")}</p>
        {(form.custom_buttons || []).map((b) => (
          <div key={b.id} data-testid={`custom-btn-row-${b.id}`} className="flex items-center justify-between border border-luxury-border px-5 py-3.5 gap-4">
            <div className="min-w-0">
              <p className="text-luxury-bone text-sm font-semibold">{b.label}</p>
              <p className="text-luxury-gray text-xs truncate" dir="ltr">{b.url}</p>
              <p className="text-luxury-gray text-xs">{b.location === "header" ? t("الشريط العلوي") : t("الواجهة الرئيسية")} — {b.style === "outline" ? t("ذهبي مفرّغ") : t("ذهبي مصمت")}</p>
            </div>
            <button type="button" data-testid={`custom-btn-delete-${b.id}`} onClick={() => setForm({ ...form, custom_buttons: (form.custom_buttons || []).filter((x) => x.id !== b.id) })}
              className="text-red-400 hover:text-red-300 p-2 shrink-0" aria-label={t("حذف الزر")}>
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        ))}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end border-t border-luxury-border pt-5">
          <div>
            <label className="block text-sm text-luxury-gray mb-2">{t("نص الزر")}</label>
            <input data-testid="custom-btn-label-input" type="text" value={newBtn.label} onChange={(e) => setNewBtn({ ...newBtn, label: e.target.value })} className={inputCls} placeholder={t("مثال: انضمي لقناتي")} />
          </div>
          <div>
            <label className="block text-sm text-luxury-gray mb-2">{t("الرابط")}</label>
            <input data-testid="custom-btn-url-input" type="url" dir="ltr" value={newBtn.url} onChange={(e) => setNewBtn({ ...newBtn, url: e.target.value })} className={inputCls} placeholder="https://…" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select data-testid="custom-btn-location-select" value={newBtn.location} onChange={(e) => setNewBtn({ ...newBtn, location: e.target.value })} className={inputCls}>
              <option value="hero">{t("الواجهة")}</option>
              <option value="header">{t("الشريط العلوي")}</option>
            </select>
            <select data-testid="custom-btn-style-select" value={newBtn.style} onChange={(e) => setNewBtn({ ...newBtn, style: e.target.value })} className={inputCls}>
              <option value="gold">{t("ذهبي مصمت")}</option>
              <option value="outline">{t("ذهبي مفرّغ")}</option>
            </select>
          </div>
          <button type="button" data-testid="custom-btn-add" onClick={() => {
            if (!newBtn.label.trim() || !newBtn.url.trim()) { toast.error(t("أدخلي نص الزر والرابط")); return; }
            const btn = { id: Date.now().toString(36), label: newBtn.label.trim(), url: newBtn.url.trim(), location: newBtn.location, style: newBtn.style };
            setForm({ ...form, custom_buttons: [...(form.custom_buttons || []), btn] });
            setNewBtn({ label: "", url: "", location: "hero", style: "gold" });
            toast.success(t("أُضيف الزر — احفظي التغييرات لتظهر على الموقع"));
          }} className="flex items-center justify-center gap-2 border border-luxury-gold/60 text-luxury-gold px-6 py-3 rounded-full text-sm font-semibold hover:bg-luxury-gold hover:text-luxury-black transition-colors">
            <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("إضافة الزر")}
          </button>
        </div>
      </section>

      <button data-testid="settings-save-button" type="submit" disabled={saving} className="flex items-center gap-3 bg-luxury-gold text-luxury-black font-semibold px-10 py-4 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" strokeWidth={1.5} />}
        {t("حفظ كل التغييرات")}
      </button>
    </form>
  );
};
